/**
 * Privacy Policy Page
 * ThePedigreeApp - POPIA Compliant
 */

import { Shield, Lock, Eye, Database, Mail } from 'lucide-react';
import { Card } from '../components/shared/UIComponents';

export const PrivacyPage = () => {
  const lastUpdated = "4 February 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-feline-600 to-feline-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-feline-200" />
          <h1 className="text-4xl font-display font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-feline-100">Last updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-feline-500" /> 1. Introduction
            </h2>
            <p className="text-gray-600">
              ThePedigreeApp ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at thepedigreeapp.co.za.
            </p>
            <p className="text-gray-600">
              By using ThePedigreeApp, you consent to the data practices described in this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-feline-500" /> 2. Information We Collect
            </h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Name and surname</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Physical address (for breeders)</li>
              <li>Cattery name and registry details (for breeders)</li>
              <li>Profile photos and cat images you upload</li>
              <li>Payment and billing information</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-feline-500" /> 3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Providing and maintaining our services</li>
              <li>Processing breeder verification</li>
              <li>Facilitating communication between buyers and breeders</li>
              <li>Processing transactions and sending related information</li>
              <li>Sending administrative emails, updates, and newsletters</li>
              <li>Responding to inquiries and support requests</li>
              <li>Improving our website and services</li>
              <li>Detecting and preventing fraud</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.1 With Other Users</h3>
            <p className="text-gray-600">
              When you list a cat or kitten for sale, certain information (cattery name, location, contact details) will be visible to potential buyers.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.2 With Service Providers</h3>
            <p className="text-gray-600">
              We may share information with third-party service providers who perform services on our behalf (hosting, analytics, payment processing).
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.3 With Registry Bodies</h3>
            <p className="text-gray-600">
              For breeder verification, we may share your registry details with SACC, CASA, CFSA, or other relevant cat registries.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">4.4 Legal Requirements</h3>
            <p className="text-gray-600">
              We may disclose information if required by law or in response to valid legal requests.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600">We implement appropriate security measures including:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>SSL/TLS encryption for data in transit</li>
              <li>Secure cloud storage with Firebase/Google Cloud</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights (POPIA Compliance)</h2>
            <p className="text-gray-600">Under the Protection of Personal Information Act (POPIA), you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Object:</strong> Object to processing of your information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              <li><strong>Lodge a Complaint:</strong> Lodge a complaint with the Information Regulator</li>
            </ul>
            <p className="text-gray-600 mt-4">To exercise these rights, contact us at privacy@thepedigreeapp.co.za.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies</h2>
            <p className="text-gray-600">We use cookies for:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the website to function</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Preference Cookies:</strong> Remember your settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-600">
              We retain your personal information for as long as necessary to provide our services. When you delete your account, we will delete or anonymise your data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600">
              ThePedigreeApp is not intended for children under 18. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy from time to time. We will notify you of changes by posting the new policy on this page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-feline-500" /> 11. Contact Us
            </h2>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="text-gray-700"><strong>ThePedigreeApp</strong></p>
              <p className="text-gray-600">Email: privacy@thepedigreeapp.co.za</p>
              <p className="text-gray-600">Website: www.thepedigreeapp.co.za</p>
              <p className="text-gray-600">Location: Western Cape, South Africa</p>
            </div>
          </section>

        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;
